/**
 * @copyright Copyright 2021 Epic Systems Corporation
 * @file Button to mark app as not implementing now (used by the Nominated Apps widget)
 * @module Epic.AppOrchard.Dashboard.MarkNowImplementingNowButton
 */
import { WarningTwoIcon } from "@chakra-ui/icons";
import {
	As,
	Box,
	Button,
	ButtonProps,
	Checkbox,
	Flex,
	FormControl,
	FormLabel,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Spacer,
	Text,
	useDisclosure,
	VStack,
} from "@chakra-ui/react";
import { ISuccessHandler, useAsync } from "@epic/react-async-hook";
import { IDispatch } from "@epic/react-redux-booster";
import { AOTooltip, ConfirmationModal, StatusAlert, TextareaWithRemainingChars } from "ao/components/Core";
import { markAsNotImplementingNow } from "ao/components/Dashboard/data";
import { WidgetActionButton } from "ao/components/Dashboard/widgetHelpers";
import { updateNotImplementingNowDetails } from "ao/store-methods/nominatedApps";
import { INominatedApp } from "ao/types";
import { isInFlight, lastExecutionErrored } from "ao/utils/useAsyncHelpers";
import React, { ChangeEvent, FC, memo, useCallback, useState } from "react";
import { AiOutlineClockCircle } from "react-icons/ai";
import { BsFillQuestionCircleFill } from "react-icons/bs";

type Props = ButtonProps & IProps;

interface IProps {
	/** If provided, use onsuccess instead of dispatch */
	app: Partial<INominatedApp>;
	orgId?: number;
	dispatch?: IDispatch;
	successHandler?: (...args: any[]) => void;
	/** If provided, use instead of default alert */
	failureHandler?: (...args: any[]) => void;
	/** button-like component to render the button as */
	buttonAs?: As;
	interestedCount?: number;
	buttonText?: string;
	excludeIcon?: boolean;
	iconColor?: string;
}

/** Button to mark app as not implementing now (used by the Nominated Apps widget) */
export const MarkNowImplementingNowButton: FC<Props> = memo((props: Props) => {
	const {
		app,
		dispatch,
		orgId,
		successHandler,
		failureHandler,
		interestedCount,
		buttonAs,
		buttonText,
		excludeIcon,
		iconColor,
		...rest
	} = props;

	//#region state and handlers
	const hasSavedNotNowDetails = app?.notNowDecision != null;
	const hasNominations =
		(interestedCount && interestedCount > 0) || (app?.nominations != null && app?.nominations.length > 0);
	const deleteNotNowHelptext = `Remove the 'not implementing now' status for this product, which will allow the product to be nominated again by users from your organization${
		hasNominations ? " and move it back into the Needs Review tab" : ""
	}.`;

	const { isOpen, onOpen, onClose } = useDisclosure();
	const [privateReason, setPrivateReason] = useState("");
	const [publicReason, setPublicReason] = useState("");
	const [notifyInterestedUsers, setNotifyInterestedUsers] = useState(true);
	const [showConfirmationDelete, setShowConfirmationDelete] = useState<boolean>(false);

	const handleToggleConfirmDelete = useCallback(
		(show: boolean) => () => setShowConfirmationDelete(show),
		[],
	);

	// populate form with any saved values from the app when it opens
	const handleOpen = useCallback(() => {
		setPrivateReason(app?.notNowDecision?.privateReason || "");
		setPublicReason(app?.notNowDecision?.publicReason || "");
		setNotifyInterestedUsers(true);
		onOpen();
	}, [app?.notNowDecision?.privateReason, app?.notNowDecision?.publicReason, onOpen]);

	const handleSetNotifyInterestedUsers = useCallback((ev: ChangeEvent<HTMLInputElement>) => {
		setNotifyInterestedUsers(ev.target.checked);
	}, []);

	const handleSetPrivateReason = useCallback((ev: ChangeEvent<HTMLTextAreaElement>) => {
		setPrivateReason(ev.target.value);
	}, []);

	const handleSetPublicReason = useCallback((ev: ChangeEvent<HTMLTextAreaElement>) => {
		setPublicReason(ev.target.value);
	}, []);

	const handleMarkAsNotImplNowSuccess = useCallback<ISuccessHandler<typeof markAsNotImplementingNow>>(
		(updatedNotNowDetails, extras) => {
			onClose();

			if (successHandler) {
				successHandler(updatedNotNowDetails);
			} else {
				const [appId, orgId] = extras.params;
				updateNotImplementingNowDetails(dispatch!, orgId, appId, updatedNotNowDetails);
			}
		},
		[dispatch, onClose, successHandler],
	);

	const handleMarkAsNotImplFailure = useCallback(() => {
		if (failureHandler) {
			failureHandler();
			onClose();
		}
	}, [failureHandler, onClose]);

	const [savingState, saveNotImplNowDetails] = useAsync(markAsNotImplementingNow, {
		executeImmediately: false,
		displayName: "markAsNotImplementingNow",
		// `extras` contains a `params` field which has the params passed to the API call
		onSuccess: (response, extras) => handleMarkAsNotImplNowSuccess(response, extras),
		onFailure: handleMarkAsNotImplFailure,
	});

	const handleSaveNotImplNowDetails = useCallback(
		(deleted: boolean = false) => () =>
			saveNotImplNowDetails(
				app?.appId!,
				orgId!,
				privateReason,
				publicReason,
				notifyInterestedUsers,
				deleted,
			),
		[app?.appId, notifyInterestedUsers, orgId, privateReason, publicReason, saveNotImplNowDetails],
	);

	const handleConfirmDeleteClick = useCallback(() => {
		setShowConfirmationDelete(false);
		handleSaveNotImplNowDetails(true)();
	}, [handleSaveNotImplNowDetails]);

	const isSaving = isInFlight(savingState);
	const hasError = failureHandler && lastExecutionErrored(savingState);

	//#endregion

	const NowImplementingNowButton = buttonAs || WidgetActionButton;

	return (
		<>
			<NowImplementingNowButton
				onClick={handleOpen}
				colorScheme="red"
				title={
					hasSavedNotNowDetails
						? "Update Not Implementing Now Details"
						: "Mark this product as not implementing now for your organization. Marking as not implementing now will prevent other people from your organization from nominating this product."
				}
				leftIcon={
					!excludeIcon && (
						<AiOutlineClockCircle
							size={20}
							style={{ color: iconColor, paddingTop: "2px", paddingBottom: "2px" }}
						/>
					)
				}
				{...rest}
			>
				{buttonText ? buttonText : "Not Implementing Now"}
			</NowImplementingNowButton>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				size="2xl"
				preserveScrollBarGap
				isCentered
				returnFocusOnClose
			>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>Mark Not Implementing Now</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<FormControl id="PrivateReason">
							<FormLabel display="inline-block" mr="0.5em">
								Private Reason
							</FormLabel>
							<AOTooltip
								helptext={`Reason you're not implementing this product right now. This is only visible to users with can purchase security.`}
							>
								<BsFillQuestionCircleFill />
							</AOTooltip>
							<TextareaWithRemainingChars
								maxChars={300}
								value={privateReason}
								onChange={handleSetPrivateReason}
							/>
						</FormControl>

						<FormControl mt="2em" id="PublicReason">
							<Flex alignItems="center">
								<FormLabel display="inline-block" mr="0.5em">
									Public Reason
								</FormLabel>
								<AOTooltip
									helptext="Reason you're not implementing this product right now. This is visible to everyone from your organization."
									boxProps={{ marginBottom: "10px" }}
								>
									<BsFillQuestionCircleFill />
								</AOTooltip>
								<Spacer />
								<WarningTwoIcon color="red" mr="2px" w="1.25em" h="1.25em" />
								<Text as="span" fontStyle="italic" fontSize="0.85em">
									This is visible to anyone from your organization.
								</Text>
							</Flex>
							<TextareaWithRemainingChars
								maxChars={300}
								isRequired
								value={publicReason}
								onChange={handleSetPublicReason}
								isInvalid={!publicReason}
							/>
						</FormControl>

						<FormControl mt="1.5em" id="NotifyInterestedUser">
							<Checkbox
								mr="0.5em"
								isChecked={notifyInterestedUsers}
								onChange={handleSetNotifyInterestedUsers}
							>
								Notify interested users
							</Checkbox>
							<AOTooltip helptext="Whether to notify users who've nominated this product that your organization is not planning on implementing it now, along with the public reason why.">
								<BsFillQuestionCircleFill />
							</AOTooltip>
						</FormControl>
					</ModalBody>

					<ModalFooter>
						<VStack>
							<Box>
								{hasSavedNotNowDetails && (
									<Button
										colorScheme="red"
										mr={2}
										onClick={handleToggleConfirmDelete(true)}
										disabled={isSaving}
										title={deleteNotNowHelptext}
									>
										Remove
									</Button>
								)}
								<Button
									loadingText="Saving"
									isLoading={isSaving}
									disabled={isSaving || !publicReason}
									title={!publicReason ? "Public reason is required to save" : ""}
									colorScheme="blue"
									mr={2}
									onClick={handleSaveNotImplNowDetails()}
								>
									Save
								</Button>
								<Button onClick={onClose} disabled={isSaving}>
									Cancel
								</Button>
							</Box>
							{hasError && (
								<Box>
									<StatusAlert
										message="Error saving the last change to the server."
										status="error"
									/>
								</Box>
							)}
						</VStack>
					</ModalFooter>
				</ModalContent>
			</Modal>
			<ConfirmationModal
				title="Unmark product as Not Implementing Now"
				message="Are you sure you want to unmark this product as 'Not Implementing Now' and allow users to nominate it again? Your organization's prior reasons for marking as 'Not Implementing Now' are still saved in the database but they will not be displayed on the website anymore if you continue."
				acceptCaption="Continue"
				cancelCaption="Cancel"
				isOpen={showConfirmationDelete}
				onCancel={handleToggleConfirmDelete(false)}
				onAccept={handleConfirmDeleteClick}
			/>
		</>
	);
});
